import { ITimeSnapshot, ITimeTravelEngine } from '../types';
import { generateUniqueId } from '../utils';
import { MemoryManager, IMemoryConfig, IMemoryUsage, IMemoryAlert } from '../memory/MemoryManager';

export interface ITimeTravelEngineConfig {
  maxSnapshots?: number;
  memoryConfig?: Partial<IMemoryConfig>;
  enableAutoCleanup?: boolean;
  compressionEnabled?: boolean;
  enableLogging?: boolean;
}

/**
 * Enhanced TimeTravelEngine - 메모리 관리가 강화된 시간여행 디버깅 엔진
 */
export class TimeTravelEngine implements ITimeTravelEngine {
  private _snapshots: Map<string, ITimeSnapshot> = new Map();
  private _maxSnapshots: number = 1000;
  private _memoryManager: MemoryManager;
  private _config: Required<ITimeTravelEngineConfig>;
  private _onMemoryAlert?: (alert: IMemoryAlert) => void;

  constructor(config: ITimeTravelEngineConfig = {}) {
    this._config = {
      maxSnapshots: config.maxSnapshots || 1000,
      memoryConfig: config.memoryConfig || {},
      enableAutoCleanup: config.enableAutoCleanup ?? true,
      compressionEnabled: config.compressionEnabled ?? true,
      enableLogging: config.enableLogging ?? false,
    };

    this._maxSnapshots = this._config.maxSnapshots;
    this._memoryManager = new MemoryManager(this._config.memoryConfig);

    // 메모리 알림 처리
    this._memoryManager.onMemoryAlert((alert) => {
      this._handleMemoryAlert(alert);
    });
  }

  /**
   * 현재 상태의 스냅샷을 생성합니다 (오버로드)
   */
  public createSnapshot(): ITimeSnapshot;
  public createSnapshot(state: Record<string, unknown>, actionType: string): ITimeSnapshot;
  public createSnapshot(state?: Record<string, unknown>, actionType?: string): ITimeSnapshot {
    if (state && actionType) {
      // 상태와 액션 타입이 모두 제공된 경우
      return this._createSnapshotWithState(state, actionType);
    } else {
      // 간편한 스냅샷 생성 (파라미터 없음)
      const currentState = typeof window !== 'undefined' ? {
        url: window.location.href,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
      } : { timestamp: Date.now() };

      return this._createSnapshotWithState(currentState, 'manual_snapshot');
    }
  }

  /**
   * 상태와 함께 스냅샷 생성 (내부 구현)
   */
  private _createSnapshotWithState(state: Record<string, unknown>, actionType: string): ITimeSnapshot {
    // 상태 압축 (옵션)
    const processedState = this._config.compressionEnabled
      ? this._memoryManager.compressSnapshot(state)
      : JSON.parse(JSON.stringify(state)); // Deep clone

    const snapshot: ITimeSnapshot = {
      id: generateUniqueId(),
      timestamp: Date.now(),
      state: processedState,
      actionType,
      description: `${actionType} at ${new Date().toISOString()}`,
    };

    // 메모리 사용량 계산
    const snapshotSize = this._memoryManager.calculateSnapshotSize(snapshot);
    
    // 개별 스냅샷 크기 체크
    const maxSnapshotSizeMB = this._config.memoryConfig.maxSnapshotSizeMB || 10;
    if (snapshotSize > maxSnapshotSizeMB) {
      if (this._config.enableLogging) {
        console.warn(`Snapshot size (${snapshotSize.toFixed(2)}MB) exceeds limit (${maxSnapshotSizeMB}MB)`);
      }
      // 필요시 추가 압축 또는 거부
    }

    this._snapshots.set(snapshot.id, snapshot);
    
    // 자동 정리 수행
    if (this._config.enableAutoCleanup) {
      this._performAutomaticCleanup();
    } else {
      this._enforceMaxSnapshots();
    }

    return snapshot;
  }

  /**
   * 스냅샷을 복원합니다
   */
  public restoreSnapshot(snapshotId: string): boolean {
    const snapshot = this._snapshots.get(snapshotId);
    if (!snapshot) {
      return false;
    }

    // 압축 해제가 필요한 경우 처리
    const restoredState = this._decompressState(snapshot.state);
    const restoredSnapshot = { ...snapshot, state: restoredState };

    this._emitRestoreEvent(restoredSnapshot);
    return true;
  }

  /**
   * 모든 스냅샷을 가져옵니다
   */
  public getAllSnapshots(): ITimeSnapshot[] {
    return Array.from(this._snapshots.values()).sort((a, b) => a.timestamp - b.timestamp);
  }

  /**
   * 모든 스냅샷을 삭제합니다
   */
  public clearSnapshots(): void {
    // 메모리 관리자에도 알림
    this._snapshots.forEach((_, id) => {
      this._memoryManager.removeSnapshot(id);
    });
    
    this._snapshots.clear();
  }

  /**
   * 특정 스냅샷을 삭제합니다
   */
  public deleteSnapshot(snapshotId: string): boolean {
    const deleted = this._snapshots.delete(snapshotId);
    if (deleted) {
      this._memoryManager.removeSnapshot(snapshotId);
    }
    return deleted;
  }

  /**
   * 메모리 사용량 정보를 가져옵니다
   */
  public getMemoryUsage(): IMemoryUsage {
    return this._memoryManager.getMemoryUsage();
  }

  /**
   * 메모리 알림 콜백을 설정합니다
   */
  public onMemoryAlert(callback: (alert: IMemoryAlert) => void): void {
    this._onMemoryAlert = callback;
  }

  /**
   * 시간 범위 기반 스냅샷 정리
   */
  public cleanupByTimeRange(olderThanMs: number): number {
    const cutoffTime = Date.now() - olderThanMs;
    const toDelete: string[] = [];

    this._snapshots.forEach((snapshot, id) => {
      if (snapshot.timestamp < cutoffTime) {
        toDelete.push(id);
      }
    });

    toDelete.forEach(id => {
      this.deleteSnapshot(id);
    });

    return toDelete.length;
  }

  /**
   * 패턴 기반 스냅샷 정리 (중복 제거)
   */
  public cleanupDuplicates(): number {
    const actionTypeGroups = new Map<string, ITimeSnapshot[]>();
    
    // 액션 타입별로 그룹화
    this._snapshots.forEach(snapshot => {
      const group = actionTypeGroups.get(snapshot.actionType) || [];
      group.push(snapshot);
      actionTypeGroups.set(snapshot.actionType, group);
    });

    let removedCount = 0;

    // 각 그룹에서 중복 제거
    actionTypeGroups.forEach((snapshots, actionType) => {
      if (snapshots.length <= 1) return;

      // 시간순 정렬
      snapshots.sort((a, b) => a.timestamp - b.timestamp);
      
      // 상태가 동일한 연속 스냅샷 제거
      for (let i = 1; i < snapshots.length; i++) {
        const prev = snapshots[i - 1];
        const current = snapshots[i];
        
        if (this._areStatesEqual(prev.state, current.state)) {
          this.deleteSnapshot(prev.id);
          removedCount++;
        }
      }
    });

    return removedCount;
  }

  /**
   * 시간여행 엔진을 시작합니다
   */
  public start(): void {
    if (this._config.enableLogging) {
      console.log('[TimeTravelEngine] Started');
    }
  }

  /**
   * 시간여행 엔진을 중지합니다
   */
  public stop(): void {
    if (this._config.enableLogging) {
      console.log('[TimeTravelEngine] Stopped');
    }
  }

  /**
   * 특정 시점으로 시간여행
   */
  public travelToTime(timestamp: number): void {
    const snapshots = this.getAllSnapshots();
    const targetSnapshot = snapshots.find(s => s.timestamp <= timestamp);
    
    if (targetSnapshot) {
      this.restoreSnapshot(targetSnapshot.id);
    } else if (this._config.enableLogging) {
      console.warn('[TimeTravelEngine] No snapshot found for timestamp:', timestamp);
    }
  }

  /**
   * 이전 이벤트로 이동
   */
  public travelToPreviousEvent(): void {
    const snapshots = this.getAllSnapshots();
    if (snapshots.length > 1) {
      const previousSnapshot = snapshots[snapshots.length - 2];
      this.restoreSnapshot(previousSnapshot.id);
    } else if (this._config.enableLogging) {
      console.warn('[TimeTravelEngine] No previous event available');
    }
  }

  /**
   * 다음 이벤트로 이동
   */
  public travelToNextEvent(): void {
    // 현재 구현에서는 "다음"의 개념이 모호함
    // 향후 현재 위치 추적 기능과 함께 구현 필요
    if (this._config.enableLogging) {
      console.warn('[TimeTravelEngine] Next event navigation not implemented yet');
    }
  }

  /**
   * 현재 상태 가져오기
   */
  public getState() {
    return {
      snapshotCount: this._snapshots.size,
      maxSnapshots: this._maxSnapshots,
      memoryUsage: this.getMemoryUsage(),
      config: this._config,
    };
  }

  /**
   * 히스토리 초기화 (clearSnapshots와 동일)
   */
  public clearHistory(): void {
    this.clearSnapshots();
  }

  /**
   * 엔진을 정리합니다
   */
  public destroy(): void {
    this.clearSnapshots();
    this._memoryManager.destroy();
  }

  /**
   * 자동 정리를 수행합니다
   */
  private _performAutomaticCleanup(): void {
    // 1. 메모리 기반 정리
    const removedByMemory = this._memoryManager.cleanup(this._snapshots);
    
    // 2. 개수 제한 정리
    this._enforceMaxSnapshots();
    
    // 3. 중복 제거 (주기적으로)
    if (this._snapshots.size > 100 && Math.random() < 0.1) { // 10% 확률
      const removedDuplicates = this.cleanupDuplicates();
      if (removedDuplicates > 0 && this._config.enableLogging) {
        console.log(`Removed ${removedDuplicates} duplicate snapshots`);
      }
    }
  }

  /**
   * 스냅샷 개수를 제한합니다
   */
  private _enforceMaxSnapshots(): void {
    if (this._snapshots.size > this._maxSnapshots) {
      const sortedSnapshots = this.getAllSnapshots();
      const oldestSnapshot = sortedSnapshots[0];
      if (oldestSnapshot) {
        this.deleteSnapshot(oldestSnapshot.id);
      }
    }
  }

  /**
   * 복원 이벤트를 발생시킵니다
   */
  private _emitRestoreEvent(snapshot: ITimeSnapshot): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('debug-time-machine:restore', {
          detail: snapshot,
        })
      );
    }
  }

  /**
   * 메모리 알림을 처리합니다
   */
  private _handleMemoryAlert(alert: IMemoryAlert): void {
    console.warn(`[Debug Time Machine] Memory Alert: ${alert.message}`, {
      currentUsage: `${alert.currentUsageMB.toFixed(2)}MB`,
      recommendation: alert.recommendedAction,
    });

    // 중요한 알림은 상위로 전달
    if (this._onMemoryAlert && alert.type === 'critical') {
      this._onMemoryAlert(alert);
    }

    // Critical 상황에서는 강제 정리
    if (alert.type === 'critical') {
      const oldCount = this._snapshots.size;
      const removed = this.cleanupByTimeRange(60000); // 1분 이전 데이터 제거
      if (this._config.enableLogging) {
        console.log(`Emergency cleanup: removed ${removed} snapshots (${oldCount} -> ${this._snapshots.size})`);
      }
    }
  }

  /**
   * 상태 압축을 해제합니다
   */
  private _decompressState(state: any): any {
    // 압축된 상태를 원래대로 복원하는 로직
    // 현재는 기본 구현만 제공
    return state;
  }

  /**
   * 두 상태가 동일한지 비교합니다
   */
  private _areStatesEqual(state1: any, state2: any): boolean {
    try {
      return JSON.stringify(state1) === JSON.stringify(state2);
    } catch {
      return false;
    }
  }
}
